import React, { useState } from 'react';
import { Calendar as CalendarIcon, Clock, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AnimateIn } from '../components/Layout';
import { consultationSlots, profileData } from '../data/mock';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { MessageCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Consultation = () => {
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [selectedTime, setSelectedTime] = useState('');
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '', company: '', email: '', phone: '', type: '', message: '',
  });
  const { toast } = useToast();

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleNext = () => {
    if (!selectedDate || !selectedTime) {
      toast({ title: "Please select a date and time slot", variant: "destructive" });
      return;
    }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast({ title: "Please fill required fields", description: "Name and Phone are mandatory.", variant: "destructive" });
      return;
    }
    try {
      await axios.post(`${API}/consultations`, {
        ...formData,
        date: selectedDate?.toISOString() || "",
        time: selectedTime,
      });
      setStep(3);
    } catch (err) {
      toast({ title: "Booking failed", description: "Please try again or contact directly.", variant: "destructive" });
    }
  };

  const disabledDays = [{ before: new Date() }];

  return (
    <div className="pt-20">
      <Toaster />
      {/* Page Hero */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimateIn>
            <span className="text-[#C9A646] font-body font-semibold text-sm tracking-[0.25em] uppercase mb-4 block">Consultation</span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#0B1F3A] leading-tight mb-4">
              Book a Consultation
            </h1>
            <p className="text-gray-500 font-body text-lg max-w-2xl leading-relaxed">
              Schedule a one-on-one consultation to discuss your compliance needs.
            </p>
          </AnimateIn>
        </div>
      </section>

      {/* Progress Steps */}
      <section className="bg-[#F8F9FA] py-8 border-y border-gray-100">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-center gap-3 sm:gap-4">
            {['Select Date & Time', 'Your Details', 'Confirmed'].map((label, idx) => (
              <React.Fragment key={idx}>
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-body font-semibold transition-colors ${
                    step > idx + 1 ? 'bg-[#C9A646] text-[#0B1F3A]' : step === idx + 1 ? 'bg-[#0B1F3A] text-white' : 'bg-gray-200 text-gray-400'
                  }`}>
                    {step > idx + 1 ? <CheckCircle size={16} /> : idx + 1}
                  </div>
                  <span className={`font-body text-sm hidden sm:block ${
                    step === idx + 1 ? 'text-[#0B1F3A] font-semibold' : 'text-gray-400'
                  }`}>{label}</span>
                </div>
                {idx < 2 && <div className="w-8 sm:w-12 h-[2px] bg-gray-200" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {step === 1 && (
            <AnimateIn>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div>
                  <h3 className="font-heading text-xl font-bold text-[#0B1F3A] mb-6 flex items-center gap-2">
                    <CalendarIcon size={20} className="text-[#C9A646]" /> Select a Date
                  </h3>
                  <div className="flex justify-center">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={disabledDays}
                      className="rounded-sm border border-gray-200 p-3"
                    />
                  </div>
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-[#0B1F3A] mb-6 flex items-center gap-2">
                    <Clock size={20} className="text-[#C9A646]" /> Select a Time
                  </h3>
                  {selectedDate ? (
                    <div className="grid grid-cols-2 gap-3">
                      {consultationSlots.map((slot, idx) => (
                        <button
                          key={idx}
                          disabled={!slot.available}
                          onClick={() => setSelectedTime(slot.time)}
                          className={`p-3 rounded-sm text-sm font-body font-medium transition-all duration-200 border ${
                            !slot.available
                              ? 'bg-gray-50 text-gray-300 cursor-not-allowed border-gray-100'
                              : selectedTime === slot.time
                              ? 'bg-[#0B1F3A] text-white border-[#0B1F3A]'
                              : 'bg-white text-[#0B1F3A] border-gray-200 hover:border-[#C9A646] hover:bg-[#C9A646]/5'
                          }`}
                        >
                          {slot.time}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-[#F8F9FA] p-8 rounded-sm text-center">
                      <CalendarIcon size={32} className="text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-400 font-body text-sm">Please select a date first</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-12 flex justify-end">
                <Button onClick={handleNext}
                  className="bg-[#C9A646] hover:bg-[#B8953B] text-[#0B1F3A] font-semibold font-body rounded-sm px-8 h-12">
                  Continue <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </AnimateIn>
          )}

          {step === 2 && (
            <AnimateIn>
              <div className="mb-8">
                <h3 className="font-heading text-2xl font-bold text-[#0B1F3A] mb-2">Your Details</h3>
                <p className="text-[#C9A646] font-body font-semibold">
                  {selectedDate?.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} at {selectedTime}
                </p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-2 block">Full Name *</label>
                    <Input name="name" value={formData.name} onChange={handleChange} placeholder="Your full name"
                      className="h-12 rounded-sm border-gray-200 font-body" />
                  </div>
                  <div>
                    <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-2 block">Company</label>
                    <Input name="company" value={formData.company} onChange={handleChange} placeholder="Company name"
                      className="h-12 rounded-sm border-gray-200 font-body" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-2 block">Email</label>
                    <Input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="your@email.com"
                      className="h-12 rounded-sm border-gray-200 font-body" />
                  </div>
                  <div>
                    <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-2 block">Phone *</label>
                    <Input name="phone" value={formData.phone} onChange={handleChange} placeholder="+91 XXXXX XXXXX"
                      className="h-12 rounded-sm border-gray-200 font-body" />
                  </div>
                </div>
                <div>
                  <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-2 block">Consultation Type</label>
                  <Select onValueChange={(val) => setFormData(prev => ({ ...prev, type: val }))}>
                    <SelectTrigger className="h-12 rounded-sm border-gray-200 font-body">
                      <SelectValue placeholder="Select type of consultation" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="labour-law-advisory">Labour Law Advisory</SelectItem>
                      <SelectItem value="compliance-audit">Compliance Audit</SelectItem>
                      <SelectItem value="statutory-compliance">Statutory Compliance</SelectItem>
                      <SelectItem value="licensing">Licensing & Registrations</SelectItem>
                      <SelectItem value="training">Training & Workshops</SelectItem>
                      <SelectItem value="new-labour-codes">New Labour Codes</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-2 block">Additional Details</label>
                  <Textarea name="message" value={formData.message} onChange={handleChange}
                    placeholder="Tell us briefly about your compliance needs..."
                    className="min-h-[120px] rounded-sm border-gray-200 font-body" />
                </div>
                <div className="flex gap-4 pt-4">
                  <Button type="button" variant="outline" onClick={() => setStep(1)}
                    className="border-2 border-gray-200 text-[#0B1F3A] hover:bg-gray-50 font-body rounded-sm px-8 h-12">
                    Back
                  </Button>
                  <Button type="submit"
                    className="bg-[#C9A646] hover:bg-[#B8953B] text-[#0B1F3A] font-semibold font-body rounded-sm px-8 h-12">
                    Confirm Booking
                  </Button>
                </div>
              </form>
            </AnimateIn>
          )}

          {step === 3 && (
            <AnimateIn>
              <div className="text-center py-12">
                <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-8">
                  <CheckCircle size={40} className="text-green-500" />
                </div>
                <h2 className="font-heading text-3xl font-bold text-[#0B1F3A] mb-4">Consultation Booked!</h2>
                <p className="text-gray-500 font-body text-lg mb-2">
                  Your consultation has been scheduled for:
                </p>
                <p className="text-[#0B1F3A] font-heading font-bold text-xl mb-1">
                  {selectedDate?.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
                <p className="text-[#C9A646] font-body font-semibold text-lg mb-8">
                  {selectedTime}
                </p>
                <p className="text-gray-500 font-body max-w-md mx-auto mb-10">
                  You'll receive a confirmation shortly. If you need to reschedule, please contact me directly.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a href={profileData.whatsapp} target="_blank" rel="noopener noreferrer">
                    <Button className="bg-green-500 hover:bg-green-600 text-white font-body rounded-sm px-8 h-11 w-full sm:w-auto">
                      <MessageCircle size={18} className="mr-2" /> Chat on WhatsApp
                    </Button>
                  </a>
                  <Button variant="outline" onClick={() => {
                    setStep(1);
                    setSelectedDate(undefined);
                    setSelectedTime('');
                    setFormData({ name: '', company: '', email: '', phone: '', type: '', message: '' });
                  }}
                    className="border-2 border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white font-body rounded-sm px-8 h-11">
                    Book Another
                  </Button>
                </div>
              </div>
            </AnimateIn>
          )}
        </div>
      </section>
    </div>
  );
};

export default Consultation;
